import { useState } from 'react';
import classNames from 'classnames';
import Container from '@ui/container/container';
import { getUserTimezone } from '@ui/calendar/utils';
import { DateTimeLayout } from '../../../templates/widgets/date-time-layout';
import type { DateTimeParts } from '../../../templates/widgets/date-time-card-widget';
import { formatDateParts, getSessionDateLabel, getSessionTimeLabel } from '../../../templates/utils/utils';
import styles from './date-time-layout-picker.module.css';

const LAYOUT_LABELS: Record<DateTimeLayout, string> = {
  [DateTimeLayout.Rows]: 'Rows',
  [DateTimeLayout.Cards]: 'Cards',
  [DateTimeLayout.Stacked]: 'Stacked',
  [DateTimeLayout.Compact]: 'Compact',
};

interface ThumbnailDates {
  date: string;
  dayFirstDate: string;
  time: string;
  parts: DateTimeParts;
}

function LayoutThumbnail({ layout, dates }: { layout: DateTimeLayout; dates: ThumbnailDates }) {
  switch (layout) {
    case DateTimeLayout.Cards:
      return (
        <div className={styles.cards}>
          {[
            ['Date', dates.date],
            ['Time', dates.time],
          ].map(([label, value]) => (
            <div key={label} className={styles.card}>
              <div className={styles.cardLabel}>
                <span className={styles.cardDot} />
                {label}
              </div>
              <div className={styles.cardValue}>{value}</div>
            </div>
          ))}
        </div>
      );
    case DateTimeLayout.Stacked:
      return (
        <div className={styles.stacked}>
          <div className={styles.stackedGroup}>
            <div className={styles.stackedLabel}>Date</div>
            <div className={styles.stackedValue}>{dates.dayFirstDate}</div>
          </div>
          <div className={styles.stackedGroup}>
            <div className={styles.stackedLabel}>Start Time</div>
            <div className={styles.stackedValue}>{dates.time}</div>
          </div>
        </div>
      );
    case DateTimeLayout.Compact:
      return (
        <div className={styles.compact}>
          <div className={styles.compactItem}>
            <span className={styles.compactSmall}>{dates.parts.shortDayName}</span>
            <span className={styles.compactLarge}>{dates.parts.shortDate}</span>
          </div>
          <span className={styles.compactSeparator} />
          <div className={styles.compactItem}>
            <span className={styles.compactSmall}>{dates.parts.period}</span>
            <span className={styles.compactLarge}>{dates.parts.time}</span>
          </div>
        </div>
      );
    default:
      return (
        <div className={styles.rows}>
          <div className={styles.rowsRow}>
            <span className={styles.rowsLabel}>Date</span>
            <span className={styles.rowsValue}>{dates.date}</span>
          </div>
          <div className={styles.rowsRow}>
            <span className={styles.rowsLabel}>Time</span>
            <span className={styles.rowsValue}>{dates.time}</span>
          </div>
        </div>
      );
  }
}

interface DateTimeLayoutPickerProps {
  value: DateTimeLayout;
  onChange: (value: DateTimeLayout) => void;
  /** the project's start, as set on the overview page - the thumbnails preview it in the event's timezone */
  startTimestamp?: number | null;
  timezone?: string;
}

export default function DateTimeLayoutPicker({ value, onChange, startTimestamp, timezone }: DateTimeLayoutPickerProps) {
  // a project with no start time yet previews the current time, and one with no timezone yet the user's own
  const [now] = useState(() => Date.now());
  const timestamp = startTimestamp || now;
  const tz = (startTimestamp && timezone) || getUserTimezone();
  const dates: ThumbnailDates = {
    date: getSessionDateLabel(timestamp, tz, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '',
    dayFirstDate:
      getSessionDateLabel(
        timestamp,
        tz,
        { day: '2-digit', month: 'short', year: 'numeric' },
        { dayFirst: true, commaBeforeYear: true }
      ) ?? '',
    time: getSessionTimeLabel(timestamp, tz) ?? '',
    parts: formatDateParts(timestamp, tz),
  };

  return (
    <Container className="flex-column gap-20">
      {Object.values(DateTimeLayout).map((layout) => (
        <button
          key={layout}
          type="button"
          className={classNames(styles.option, { [styles.selected]: layout === value })}
          aria-label={`${LAYOUT_LABELS[layout]} layout`}
          aria-pressed={layout === value}
          onClick={() => onChange(layout)}
        >
          <LayoutThumbnail layout={layout} dates={dates} />
        </button>
      ))}
    </Container>
  );
}
